import { useState } from "react";
import { Link } from "react-router-dom";
import {
  finalizeTreatmentComment,
  PUBLISH_TO_PATIENT_CTA,
  TREATMENT_COMMENT_LABEL,
  treatmentCommentPrefill,
  TREATMENT_END_REASON_LABEL,
  TREATMENT_STATUS_OUTCOME_LABEL,
  treatmentStatusOutcomesFor,
  type PublishTreatmentStatusDecisionResult,
  type TreatmentEndReason,
  type TreatmentStatusOutcome,
} from "@care-by-ella/contracts";
import {
  publishTreatmentStatusDecision,
  UnauthorizedError,
  ValidationError,
  type PatientDetail,
} from "@/lib/clinician-api";
import { doctorNoticeText } from "@/lib/doctor-notice";
import { errorText } from "@/lib/error-text";
import { patientHref } from "@/lib/patient-links";
import { cycleStateLabel, treatmentStateLabel } from "@/lib/state-labels";
import {
  STATUS_OUTCOME_EFFECTS,
  statusDecisionBody,
  statusDecisionErrors,
  statusSubscriptionEffect,
} from "@/lib/treatment-status-decision";
import { TreatmentCommentField } from "./TreatmentCommentField";
import { TreatmentStatusChain } from "./TreatmentStatusChain";
import {
  EMPTY_ORDER,
  orderComplete,
  orderFieldErrors,
  PrescriptionOrderFields,
  toOrder,
  type OrderDraft,
} from "./PrescriptionOrderFields";
import { hiddenWhenReadOnly } from "./SupportView";
import { WebdocControlStep } from "./WebdocControlStep";

// TES-1291 (TES-1267 §4) — the decision workspace for a treatment that is
// already running. A-02/09C §4:
//   "Samma adminflöde används vid … medicinsk månadsuppföljning,
//    receptförnyelse, dosjustering, läkemedelsbyte, behandlingsuppehåll och
//    avslut."
// The same sections in the same order as the first decision: the status
// chain, the current treatment, the outcome, the doctor's text to the
// patient, the Webdoc confirmation, and a preview before the publish.
//
// Pause, resumption, end and a medication switch are decided here. The
// switch picks the new product, then its dose, then its interval from the
// medication catalogue (TES-1292), in the same order fields as every other
// order: free text while the product's entry is not clinically approved
// (`catalogueVersionInForce`). The monthly review, renewal and dose change
// are decided in the review on the monthly check-in tab
// (RenewalReviewPanel), which this links to.

const OPEN_CYCLE_STATES = [
  "active",
  "paused",
  "expiring_soon",
  "renewal_pending",
  "doctor_review",
];

const FIELD = (key: string) => `treatment-status-decision-${key}`;

function FieldErrorText({ message }: { message: string | undefined }) {
  return message ? <p className="text-xs text-danger">{message}</p> : null;
}

function StatusDecisionEditor({
  patientId,
  data,
  onClose,
  onPublished,
  onUnauthorized,
}: {
  patientId: string;
  data: PatientDetail;
  onClose: () => void;
  onPublished: (result: PublishTreatmentStatusDecisionResult) => void;
  onUnauthorized: () => void;
}) {
  const treatmentState = data.treatment?.state ?? null;
  const outcomes = treatmentStatusOutcomesFor(treatmentState);
  // Nothing preselected: each outcome is a clinical decision.
  const [outcome, setOutcome] = useState<TreatmentStatusOutcome | null>(null);
  // TES-1554 — an end's reason, asked only while the server lists reasons
  // (the tenant flag). Nothing preselected: the reason with a financial
  // effect is always the doctor's own choice.
  const endReasons = data.treatmentEndReasons ?? [];
  const [endReason, setEndReason] = useState<TreatmentEndReason | null>(null);
  // TES-1290 — "Läkarens kommentar till behandlingsbeslutet", prefilled per
  // outcome. Null until the doctor types in it, so a changed outcome
  // changes the proposal.
  const [comment, setComment] = useState<string | null>(null);
  const commentPrefill = treatmentCommentPrefill(outcome);
  const summary = doctorNoticeText(comment, commentPrefill);
  const publishedComment = finalizeTreatmentComment(summary);
  const [journalDone, setJournalDone] = useState(false);
  // A switch's new order. Empty: the new product is the doctor's choice.
  const [order, setOrder] = useState<OrderDraft>(EMPTY_ORDER);
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The prescription period the decision acts on: the newest open one.
  const cycles = [...(data.treatment?.cycles ?? [])].sort(
    (a, b) => b.cycleNumber - a.cycleNumber,
  );
  const cycle =
    cycles.find((c) => OPEN_CYCLE_STATES.includes(c.state)) ??
    cycles[0] ??
    null;

  const draft = {
    outcome,
    patientSummary: summary,
    journalDone,
    order: orderComplete(order) ? toOrder(order) : null,
    currentMedication: cycle?.medication ?? null,
    endReasons,
    endReason,
  };
  const errors = attempted ? statusDecisionErrors(draft) : {};

  async function publish() {
    const body = statusDecisionBody(draft);
    if (!body) {
      setAttempted(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onPublished(await publishTreatmentStatusDecision(patientId, body));
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      if (e instanceof ValidationError) {
        setError(
          "Beslutet kunde inte publiceras. Kontrollera uppgifterna och försök igen.",
        );
        return;
      }
      setError(errorText(e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  }

  const effects = outcome ? STATUS_OUTCOME_EFFECTS[outcome] : null;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/30 sm:items-center sm:p-md">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="treatment-status-decision-title"
        className="flex h-full w-full flex-col gap-md overflow-y-auto bg-surface p-md sm:h-auto sm:max-h-[90vh] sm:max-w-xl sm:rounded-md sm:p-lg"
      >
        <h3
          id="treatment-status-decision-title"
          className="font-display text-xl"
        >
          Behandlingsbeslut
        </h3>
        <p className="-mt-sm text-sm text-textSecondary">
          för{" "}
          <Link
            to={patientHref(patientId)}
            onClick={onClose}
            className="font-semibold text-brand hover:underline"
          >
            {data.patient.name ??
              `Patient …${data.patient.personalNumber.slice(-6)}`}
          </Link>
        </p>

        <TreatmentStatusChain
          startState={data.treatmentStartState}
          plannedMedication={false}
        />

        {/* 1) Aktuell behandling. */}
        <section className="rounded-md border border-surfaceMuted p-sm text-sm">
          <p className="font-semibold">Aktuell behandling</p>
          <p className="mt-xs">
            Behandlingsstatus:{" "}
            <span className="font-semibold">
              {treatmentStateLabel(treatmentState ?? "")}
            </span>
          </p>
          {cycle ? (
            <p className="mt-xs">
              Receptperiod {cycle.cycleNumber}:{" "}
              {cycle.medication ? `${cycle.medication} ` : ""}
              {cycle.dose} · {cycleStateLabel(cycle.state)}
            </p>
          ) : null}
          <p className="mt-xs text-xs text-textSecondary">
            Månadsuppföljning, receptförnyelse och dosjustering beslutas än så
            länge under{" "}
            <Link
              to={patientHref(patientId, "manad")}
              onClick={onClose}
              className="font-semibold text-brand hover:underline"
            >
              Månadsincheckning
            </Link>
            .
          </p>
        </section>

        {/* 2) Strukturerat utfall. */}
        <fieldset id={FIELD("outcome")} className="flex flex-col gap-xs">
          <legend className="mb-xs text-sm font-semibold">Beslut</legend>
          {outcomes.map((o) => (
            <label
              key={o}
              className={`flex cursor-pointer items-start gap-sm rounded-md border p-sm text-sm ${
                outcome === o
                  ? "border-brand bg-brand-accent"
                  : "border-surfaceMuted"
              }`}
            >
              <input
                type="radio"
                name={`treatment-status-decision-${patientId}`}
                className="mt-[3px] accent-brand"
                checked={outcome === o}
                disabled={busy}
                onChange={() => setOutcome(o)}
              />
              <span className="font-semibold">
                {TREATMENT_STATUS_OUTCOME_LABEL[o]}
              </span>
            </label>
          ))}
          <FieldErrorText message={errors.outcome} />
        </fieldset>

        {/* TES-1554 — why the drug treatment ends. Only "not suitable"
            waives the binding and gives admin the refund task. */}
        {outcome === "end" && endReasons.length > 0 ? (
          <fieldset
            id={FIELD("end-reason")}
            className="flex flex-col gap-xs"
            data-testid="treatment-status-decision-end-reason"
          >
            <legend className="mb-xs text-sm font-semibold">
              Anledning till avslutet
            </legend>
            {endReasons.map((r) => (
              <label
                key={r}
                className={`flex cursor-pointer items-start gap-sm rounded-md border p-sm text-sm ${
                  endReason === r
                    ? "border-brand bg-brand-accent"
                    : "border-surfaceMuted"
                }`}
              >
                <input
                  type="radio"
                  name={`treatment-status-decision-end-reason-${patientId}`}
                  className="mt-[3px] accent-brand"
                  checked={endReason === r}
                  disabled={busy}
                  onChange={() => setEndReason(r)}
                />
                <span>{TREATMENT_END_REASON_LABEL[r]}</span>
              </label>
            ))}
            <FieldErrorText message={errors.endReason} />
          </fieldset>
        ) : null}

        {/* 3) Recept: a switch's new order, product → dose → interval. */}
        {outcome === "switch" ? (
          <section
            id={FIELD("order")}
            className="flex flex-col gap-xs text-sm"
            data-testid="treatment-status-decision-order"
          >
            <p className="font-semibold">Nytt läkemedel</p>
            <PrescriptionOrderFields
              idPrefix={FIELD("order")}
              value={order}
              onChange={setOrder}
              disabled={busy}
              errors={attempted ? orderFieldErrors(order) : undefined}
            />
            <FieldErrorText message={errors.order} />
          </section>
        ) : null}

        {/* 4) TES-1290 — "Läkarens kommentar till behandlingsbeslutet",
            prefilled per outcome. Previewed with the effects below. */}
        <TreatmentCommentField
          id={FIELD("summary")}
          text={summary}
          value={comment}
          prefill={commentPrefill}
          onChange={setComment}
          structured={
            outcome === "switch"
              ? toOrder(order)
              : cycle
                ? { dose: cycle.dose }
                : undefined
          }
          disabled={busy}
          error={errors.summary}
          preview={false}
        />

        {/* 5) Webdoc. */}
        {outcome ? (
          <div id={FIELD("webdoc")}>
            <WebdocControlStep
              idPrefix="treatment-status-decision"
              journal={{ done: journalDone, onChange: setJournalDone }}
              labOrder={false}
              labOrderDone={false}
              onLabOrderDoneChange={() => undefined}
              disabled={busy}
            />
            <FieldErrorText message={errors.webdoc} />
          </div>
        ) : null}

        {/* 6) Preview: what the publish changes for her (A-02 §7). */}
        {effects ? (
          <section
            className="rounded-md border border-surfaceMuted bg-surfaceMuted/30 p-sm text-sm"
            data-testid="treatment-status-decision-preview"
          >
            <p className="font-semibold">När du publicerar</p>
            <dl className="mt-xs grid grid-cols-[auto_1fr] gap-x-md gap-y-xs">
              <dt className="text-textSecondary">Din behandling</dt>
              <dd>{effects.dinBehandling}</dd>
              <dt className="text-textSecondary">Hem</dt>
              <dd>{effects.hem}</dd>
              <dt className="text-textSecondary">Historik</dt>
              <dd>{effects.history}</dd>
              <dt className="text-textSecondary">Abonnemang</dt>
              <dd>{statusSubscriptionEffect(outcome!, endReason)}</dd>
            </dl>
            {publishedComment ? (
              <>
                <p className="mt-sm text-xs text-textSecondary">
                  {TREATMENT_COMMENT_LABEL}
                </p>
                <p className="mt-xs whitespace-pre-wrap rounded-sm border border-surfaceMuted bg-surface p-sm">
                  {publishedComment}
                </p>
              </>
            ) : null}
          </section>
        ) : null}

        {error ? (
          <p className="rounded-md border border-danger/30 bg-danger/10 p-sm text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-sm">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md px-md py-sm text-sm text-textSecondary hover:bg-surfaceMuted"
          >
            Avbryt
          </button>
          <button
            type="button"
            onClick={() => void publish()}
            disabled={busy}
            className={`rounded-md px-md py-sm text-sm text-white hover:opacity-90 disabled:opacity-60 ${
              outcome === "end" ? "bg-danger" : "bg-brand text-textOnBrand"
            }`}
          >
            {busy ? "Publicerar…" : PUBLISH_TO_PATIENT_CTA}
          </button>
        </div>
      </div>
    </div>
  );
}

// Hidden in the admin's read-only support view: the decision is published to
// the patient (TES-1040).
export const TreatmentStatusDecisionEditor =
  hiddenWhenReadOnly(StatusDecisionEditor);
